
<script setup>
import { ref, watch } from "vue"
import { useRouter } from "vue-router"
import { getCurrentInstance } from "vue"
import { useToastr } from "@/composables/useToastr"
import axiosIns from "@/plugins/axios.js"

const id_applicant = JSON.parse(localStorage.getItem('userData'))?.id_usuario

const toastr = useToastr()
const props = defineProps({
  title: {
    type: String,
    default: "Auxiliar de Cosecha",
  },
  vacancies: {
    type: String,
    default: 0,
  },
  place: {
    type: String,
    default: "Olmos",
  },
  image: {
    type: String,
    default: "",
  },
  id: {
    type: Number,
  },
  isfavorite: {
    type: Boolean,
    default: false
  },
  isapplicated: {
    type: Boolean,
    default: false
  }
})

const router = useRouter();

const localFavorite = ref(props.isfavorite)
const localApplicated = ref(props.isapplicated)

const handleApply = () => {
  router.push({
    path: "/dashboard_detalle_oferta",
    query: { id: props.id },
  });
}

// Clic en cualquier parte de la tarjeta (fuera de favorito/compartir): lleva al detalle
// de la oferta. Si ya postuló, esa misma vista muestra el botón como "Postulado" y bloqueado
// en vez de dejarlo postular de nuevo.
const handleCardClick = () => {
  handleApply()
}

const processToFavorites = () => {
  localFavorite.value = !localFavorite.value
  const params = {
    offer_id : props.id,
    applicant_id : id_applicant,
  }

  const config = {
      method: "post",
      url: "/talentoagv/processFavoriteOffer/",
      headers: {
          'Content-Type': 'application/json'
      },
      data: params
  };

  axiosIns(config)
  .then(function (response) {
    if (response.status === 200) {
      const resultado = response.data["result"];
      if(localFavorite.value == true){
        toastr.success("Agregado a favoritos.✍🏻", "Operación exitosa.👍🏻");
      }else{
        toastr.success("Eliminado de favoritos.✍🏻", "Operación exitosa.👍🏻");
      }
    }
  })
  .catch(function (error) {
      console.log(error);
  });
  
}

const shareJob = () => {
  const shareUrl = `${window.location.origin}/dashboard_detalle_oferta?id=${props.id}`;
  if (navigator.share) {
      navigator.share({
          title: props.title,
          text: `Revisa esta oferta de trabajo: ${props.title}`,
          url: shareUrl,
      });
  } else {
      navigator.clipboard.writeText(shareUrl);
      toastr.success("Enlace copiado al portapapeles.", "Listo");
  }
};

watch(
  () => props.isfavorite,
  (newVal) => {
    localFavorite.value = newVal
  }
)

watch(
  () => props.isapplicated,
  (newVal) => {
    localApplicated.value = newVal
  }
)

</script>
<template>
  <article class="job-card" style="cursor:pointer" @click="handleCardClick">
    <div class="job-card__img">
      <img :src="image" :alt="title" />
    </div>

    <h3 class="job-card__title">{{ title }}</h3>

    <p class="job-card__meta">
      <span>{{ vacancies }} vacante<span v-if="vacancies !== 1">s</span></span>
      <span class="dot">|</span>
      <span>{{ place }}</span>
    </p>

    <div class="job-card__footer">
      <button
        v-if="id_applicant"
        class="icon-btn"
        type="button"
        aria-label="Favorito"
        :disabled="!id_applicant"
        @click.stop="processToFavorites"
      >
      <i v-if="!localFavorite" class="fa-regular fa-heart"></i>
      <i v-else class="fa-solid fa-heart"></i>
      </button>
      <button
        class="icon-btn"
        type="button"
        aria-label="Compartir"
        @click.stop="shareJob"
      >
        <i class="bx bx-share-alt"></i>
      </button>

      <button 
      v-if="!localApplicated" 
      class="apply-btn"
      type="button"
      @click.stop="handleApply"
      >
        Postular
      </button>
      <button
      v-else
      class="applied-btn text-muted"
      type="button"
      @click.stop="handleCardClick"
      >
        Postulado
      </button>
    </div>
  </article>
</template>
<style scoped>
.job-card {
    position: relative;
    overflow: visible;
    background: #fff;
    border: 1px solid rgba(10, 30, 70, 0.15);
    border-radius: 18px;
    padding: 14px;
    padding-top: 120px;
    width: 100%;
    max-width: 260px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
    display: flex;
    flex-direction: column;
}

.job-card:hover {
    border-color: var(--bg-primary-as-light);
    box-shadow: 0 8px 24px rgba(25, 87, 209, 0.12);
}

.job-card__img {
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 85%;
    height: 180px;
    border-radius: 12px;
    overflow: hidden;
    background: #f3f5f8;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
.job-card__img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.job-card__title {
    font-size: 16px;
    font-weight: 700;
    color: #0a1e46;
    margin: 8px 0 6px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: calc(1.35em * 2);
}

.job-card__meta {
    margin: 0 0 12px;
    color: #6a7385;
    font-size: 13px;
    line-height: 1.3;
    min-height: 1.3em;
}
.job-card__meta .dot {
    margin: 0 3px;
}

.job-card__footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 10px;
}

.icon-btn {
    border: 1px solid rgba(10, 30, 70, 0.12);
    background: #fff;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.icon-btn i {
    font-size: 18px;
    color: var(--bg-primary-as-light);
}

.icon-btn:hover {
    background: rgba(25, 87, 209, 0.06);
    border-color: rgba(25, 87, 209, 0.35);
}

.icon-btn:disabled,
.icon-btn[disabled] {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
}

.apply-btn {
    margin-left: auto;
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid var(--bg-primary-as-light);
    background: #fff;
    color: var(--bg-primary-as-light);
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.apply-btn:hover {
    background: var(--bg-primary-as-light);
    color: #fff;
}

.applied-btn{
    margin-left: auto;
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid #6B7385;
    background: #fff;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}
</style>